/**
 * Reactions on a node of the canvas, as on the cards of the Ideas page (utils/ideas.ts): the same
 * six emojis, each with the people who gave it. They are kept in the node's data, saved with it,
 * and seen by everyone the workspace is shared with.
 */
import { REACTIONS } from './ideas';

/** Emoji → the ids of the people who gave it */
export type NodeReactions = Record<string, string[] | undefined>;

/**
 * One person's reaction added, or taken back. An emoji nobody gives any more is set to undefined
 * rather than left out: that is how a node's save takes a field off in the cloud
 * (utils/storage.ts withClearedKeys).
 */
export const toggleNodeReaction = (reactions: NodeReactions | undefined, emoji: string, userId: string): NodeReactions => {
    const current = reactions ?? {};
    if (!(REACTIONS as readonly string[]).includes(emoji)) return current;
    const had = current[emoji] ?? [];
    const who = had.includes(userId) ? had.filter(id => id !== userId) : [...had, userId];
    return { ...current, [emoji]: who.length ? who : undefined };
};

export interface ShownReaction {
    emoji: string;
    count: number;
    /** The signed-in person gave it */
    mine: boolean;
    who: string[];
}

/** The reactions to show, in the order they are offered */
export const nodeReactionsOf = (reactions: NodeReactions | undefined, myId: string | undefined): ShownReaction[] =>
    REACTIONS.flatMap(emoji => {
        const who = reactions?.[emoji] ?? [];
        return who.length ? [{ emoji, count: who.length, mine: !!myId && who.includes(myId), who }] : [];
    });

// ── In the cloud ─────────────────────────────────────────────────────────────
// A reaction kept in the node's data is saved with the workspace, which only its editors may
// write. So that those who may only view a workspace react too, a reaction is since kept as a
// record of its own beside the comments (utils/reactionStore.ts); the ones in the nodes' data
// from before are still shown, and an editor can still take their own back.

/** One person's reaction on one node, as kept in the cloud */
export interface NodeReaction {
    id: string;
    nodeId: string;
    emoji: string;
    userId: string;
    createdAt: number;
}

/** The record's id: one per node, person and emoji, so giving and taking back never need a query */
export const reactionKey = (nodeId: string, userId: string, emoji: string): string =>
    `${nodeId}__${userId}__${(REACTIONS as readonly string[]).indexOf(emoji)}`;

/** A reaction as read back from the cloud, or null when it is not one */
export const toReaction = (id: string, data: Record<string, unknown>): NodeReaction | null => {
    if (typeof data.nodeId !== 'string' || typeof data.userId !== 'string' || typeof data.emoji !== 'string') return null;
    if (!(REACTIONS as readonly string[]).includes(data.emoji)) return null;
    return { id, nodeId: data.nodeId, emoji: data.emoji, userId: data.userId, createdAt: typeof data.createdAt === 'number' ? data.createdAt : 0 };
};

/**
 * Every node's reactions as shown: those kept in its data from before, and those in the cloud,
 * each person counted once for an emoji. Only nodes with any reaction are in the result.
 */
export const mergeNodeReactions = (legacy: ReadonlyArray<readonly [string, NodeReactions | undefined]>, cloud: ReadonlyArray<NodeReaction>): Record<string, NodeReactions> => {
    const merged: Record<string, NodeReactions> = {};
    for (const [nodeId, reactions] of legacy) {
        if (!reactions) continue;
        for (const [emoji, who] of Object.entries(reactions)) {
            if (!who?.length) continue;
            (merged[nodeId] ??= {})[emoji] = [...who];
        }
    }
    for (const reaction of [...cloud].sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id))) {
        const who = (merged[reaction.nodeId] ??= {})[reaction.emoji] ?? [];
        if (!who.includes(reaction.userId)) merged[reaction.nodeId][reaction.emoji] = [...who, reaction.userId];
    }
    return merged;
};

/** Who gave a reaction, by name: "You, Ana and Bo" */
export const reactorsLabel = (who: string[], myId: string | undefined, names: Record<string, string> = {}): string => {
    const named = who.map(id => (id === myId ? 'You' : names[id] || 'Someone'));
    const mineFirst = [...named.filter(name => name === 'You'), ...named.filter(name => name !== 'You')];
    return mineFirst.length <= 1 ? mineFirst.join('') : `${mineFirst.slice(0, -1).join(', ')} and ${mineFirst[mineFirst.length - 1]}`;
};
